import React from 'react';
import { useRouter } from 'next/router';
import { useForm, useWatch } from 'react-hook-form';
import {
	QueryObserverResult,
	RefetchOptions,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import {
	useInfinityPagination,
	useNotification,
	usePhaseStatus,
} from '@/hooks';
import { Button, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { PayIndividually } from './PayIndividually';
import { formatCurrencyAlt, getValidCaseId } from '@/utils';
import {
	auditService,
	casesLawyerService,
	GET_CASE_STEPS_STATUS,
	GET_LAWYER_CASE_DETAILS,
	GET_PAYMENT_SUMMARY,
	GET_SETTLEMENT_SUMMARY,
	GET_USERS_TO_PAY,
} from '@/api';
import {
	ApprovePhase,
	CaseSettlementDetails,
	HttpResponse,
	Option,
	PaymentMethodEnum,
	SettlementSummary,
	StepStatusCaseData,
	StepStatusCaseType,
	UsersToPay,
	UserToPayItem,
} from '@/interfaces';
import { PaymentGroup } from './PaymentGroup';
import { CaseContext } from '@/context/CaseContext';
import { InputList } from '@/components/form';
import useModal from '@/hooks/useModal';
import { ModalHeader } from '@/components/dashboard';
import { PayConfirmModal } from './PayConfirmModal';
import { useDebouncedCallback } from 'use-debounce';
import { useApplyFilterMultiple } from '@/hooks/filters/useApplyFilterMultiple';
import { format, parseISO } from 'date-fns';

interface Props {
	caseSteps?: StepStatusCaseData;
	statusStep: StepStatusCaseType;
	settlementDetail: HttpResponse<CaseSettlementDetails> | undefined;
	settlementSummary: SettlementSummary | undefined;
	refetchSettlement: (
		options?: RefetchOptions | undefined
	) => Promise<QueryObserverResult<HttpResponse<CaseSettlementDetails>, Error>>;
}

const options: Option[] = [
	{
		name: PaymentMethodEnum.INDIVIDUAL,
		label: 'Pay Individually',
	},
	{
		name: PaymentMethodEnum.GROUP,
		label: 'Pay Group',
	},
	{
		name: PaymentMethodEnum.EXTERNAL,
		label: 'External Administrator',
	},
];

export const PaymentDetails: React.FC<Props> = ({
	statusStep,
	caseSteps,
	settlementDetail,
	settlementSummary,
	refetchSettlement,
}) => {
	const router = useRouter();
	const { id } = router.query;
	const queryClient = useQueryClient();
	const { isCaseReadOnly, setSaveStatus } = React.useContext(CaseContext);
	const { toastError, toastSuccess } = useNotification();
	const { selected: stateIds } = useApplyFilterMultiple({ filterKey: 'state' });
	const [usersList, setUsersList] = React.useState<UserToPayItem[]>([]);
	const [error, setError] = React.useState<{ message: string } | null>(null);
	const [search, setSearch] = React.useState('');
	const [inputError, setInputError] = React.useState<{
		message: string;
	} | null>(null);
	const [optionSelected, setOptionSelected] =
		React.useState<PaymentMethodEnum>();
	const [users, setUsers] = React.useState<UserToPayItem[]>([]);

	const { getValues, reset, register, setValue, watch, control } = useForm({
		mode: 'onChange',
	});

	const formValues = useWatch({
		control,
	});

	const {
		Modal: ModalPayGroupConfirm,
		show: showPayGroupConfirmModal,
		hide: hidePayGroupConfirmModal,
	} = useModal();

	const { phaseId, stepId } = usePhaseStatus({
		caseSteps,
		phaseName: 'Settlement Phase',
		stepName: 'Payment Details',
	});

	const isApproved = statusStep === 'approved';
	const paymentMethod = settlementDetail?.data.payment_method;
	const groupAmount = settlementDetail?.data.group_amount;
	// const isPreview =
	// 	paymentMethod !== PaymentMethodEnum.GROUP ||
	// (paymentMethod === PaymentMethodEnum.GROUP && groupAmount !== 0);

	const { data: paymentSummary } = useQuery({
		queryKey: [GET_PAYMENT_SUMMARY],
		queryFn: () => casesLawyerService.getPaymentSummary(id as string),
	});

	const fetchData = async ({ pageParam = 1 }): Promise<UsersToPay> => {
		const response = await casesLawyerService.getUsersToPay(
			id as string,
			pageParam,
			50,
			search,
			stateIds.length > 0 ? stateIds.map((item) => Number(item)) : undefined
		);
		return response.data;
	};

	const {
		data: usersData,
		refetch: refetchUserData,
		totalItems,
		lastItemCallback,
	} = useInfinityPagination({
		queryKey: [GET_USERS_TO_PAY, id, search, stateIds],
		queryFn: fetchData,
	});

	// const { data: users, refetch: refetchUserData } = useQuery({
	// 	queryKey: [GET_USERS_TO_PAY, search, stateIds],
	// 	queryFn: () =>
	// 		casesLawyerService.getUsersToPay(
	// 			id as string,
	// 			1,
	// 			500,
	// 			search,
	// 			stateIds.length > 0 ? stateIds.map((item) => Number(item)) : undefined
	// 		),
	// });

	// React.useEffect(() => {
	// 	if (!paymentMethod) return;

	// 	if (isPreview) {
	// 		setOptionSelected(paymentMethod);
	// 	}
	// }, [paymentMethod]);

	React.useEffect(() => {
		if (!usersData) return;
		const hasAmount = paymentMethod !== PaymentMethodEnum.EXTERNAL;

		setUsers(usersData.pages.flatMap((page) => page.items));
		setUsersList(
			usersData.pages
				.flatMap((page) => page.items)
				.map((item) => ({
					...item,
					formattedAmount: hasAmount
						? formatCurrencyAlt(String(item?.amount), 'blur')
						: '',
					error: null,
				}))
		);
	}, [usersData]);

	React.useEffect(() => {
		reset({
			amount: groupAmount,
		});
	}, [groupAmount]);

	const refetchQueries = async () => {
		await refetchSettlement();
		await refetchUserData();
		const caseId = getValidCaseId(id);
		if (caseId) {
			queryClient.refetchQueries({
				queryKey: [GET_CASE_STEPS_STATUS, caseId],
			});
			queryClient.refetchQueries({
				queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
			});
		}
		queryClient.refetchQueries({
			queryKey: [GET_SETTLEMENT_SUMMARY, id],
		});
	};

	React.useEffect(() => {
		if (!settlementDetail) return;

		// if the amount is changed, call the debounced function
		if (getIsAmountChanged()) {
			debouncedFunction();
		}
	}, [formValues, settlementDetail]);

	React.useEffect(() => {
		if (!optionSelected || !settlementDetail) return;

		// if the payment method is changed, call the debounced function
		if (optionSelected !== settlementDetail?.data.payment_method) {
			debouncedFunction();
		}
	}, [optionSelected, settlementDetail]);

	// -------------------- Functions --------------------//

	const getPaymentInfo = () => {
		const currentAmount = (getValues('amount') || '0').toString();
		const amount =
			optionSelected !== PaymentMethodEnum.EXTERNAL
				? Number(currentAmount.replace(/[^0-9.]/g, ''))
				: 0;
		const isUpdatedAmount =
			optionSelected === PaymentMethodEnum.GROUP ||
			optionSelected === PaymentMethodEnum.EXTERNAL;

		return {
			isUpdatedAmount,
			amount,
		};
	};

	const debouncedFunction = useDebouncedCallback(() => {
		handleSave();
	}, 1000);

	const handleSave = async () => {
		// const isError = handleIsError();
		// console.log({ isError });
		// if (isError) return;
		// const { amount, isUpdatedAmount } = getPaymentInfo();
		const { amount } = getPaymentInfo();
		setSaveStatus({ isSaving: true });
		try {
			const data = {
				payment_method: optionSelected as PaymentMethodEnum,
				amount: optionSelected !== PaymentMethodEnum.EXTERNAL ? amount : 0,
				// user_payments: isUpdatedAmount
				// 	? usersList.map((user) => ({ ...user, amount }))
				// 	: usersList,
			};
			await casesLawyerService.completePaymentUsers(id as string, data);
			if (optionSelected === PaymentMethodEnum.EXTERNAL) {
				await handleApprove();
			}
			refetchQueries();
		} catch (error) {
			setSaveStatus({ isError: true });
			toastError(
				'An error occurred while saving the amounts. Please try again.'
			);
		} finally {
			setSaveStatus({ isSaving: false });
		}
	};

	const handleIsError = (): boolean => {
		if (!optionSelected) {
			setError({
				message: 'Please fill out the required field.',
			});
			return true;
		}

		if (usersList.length === 0) return true;

		if (optionSelected === PaymentMethodEnum.GROUP) {
			const currentAmount: string = getValues('amount').toString();
			if (!currentAmount) {
				setInputError({
					message: 'Please fill out the required field.',
				});
				return true;
			}

			if (currentAmount.startsWith('0')) {
				setInputError({
					message: 'Please enter an amount greater than 0.',
				});
				return true;
			}
		}

		if (optionSelected === PaymentMethodEnum.INDIVIDUAL) {
			let isErrorEmpty = 0;
			const count = usersList.reduce((acc, item) => {
				if (item.amount === null) isErrorEmpty += 1;
				return !item.amount || item.amount <= 0 ? acc + 1 : acc;
			}, 0);

			const isSomeEmpty = usersList.length == 0 || count > 0;
			if (optionSelected === PaymentMethodEnum.INDIVIDUAL && isSomeEmpty) {
				if (isErrorEmpty > 0) {
					const newUserList = usersList.map((user) => {
						const newUser = { ...user };
						newUser.error =
							user.amount === null
								? { ...newUser.error, message: 'true' }
								: { ...newUser.error, message: '' };
						return newUser;
					});

					setUsersList(newUserList);
					setError({
						message: `There are ${isErrorEmpty} clients with pending payment details. Please ensure all details are entered to proceed.`,
					});
				}
				if (!isErrorEmpty) {
					const newUserList = usersList.map((user) => {
						const newUser = { ...user };
						newUser.error =
							!user.amount || user.amount <= 0
								? { ...newUser.error, message: 'true' }
								: { ...newUser.error, message: '' };
						return newUser;
					});
					setUsersList(newUserList);

					setError({
						message: `There are ${count} users with a payment amount of zero. Please set an amount greater than zero.`,
					});
				}

				return true;
			}
		}

		setError(null);
		return false;
	};

	const handleSelectMethod = (method: PaymentMethodEnum) => {
		setOptionSelected(method);
		setError(null);
	};

	const getIsAmountChanged = () => {
		if (usersList.length === 0 || !optionSelected) return false;

		// if (optionSelected === PaymentMethodEnum.INDIVIDUAL) {
		// 	const currentAmount = (getValues('amount') || 0)?.toString();
		// 	const amount = Number(currentAmount?.replace(/[^0-9.]/g, ''));
		// 	return amount !== usersList[0].amount;
		// }

		if (optionSelected === PaymentMethodEnum.GROUP) {
			return usersList.some((user) => {
				const find = users.find(
					(item) => item.application_id === user.application_id
				);
				if (find) {
					return find.amount !== user.amount;
				}
				return false;
			});
		}

		return false;
	};

	const handlePaySettlement = async () => {
		const isError = handleIsError();
		if (isError) return;
		try {
			await casesLawyerService.confirmPaySettlement(id as string);
			await handleApprove();
			refetchQueries();
			toastSuccess('The settlement has been successfully paid.');
			hidePayGroupConfirmModal();
		} catch (error) {
			toastError(
				'An error occurred while processing the settlement payment. Please try again.'
			);
		}
	};

	const handleApprove = async () => {
		try {
			if (id && phaseId && stepId) {
				const data: ApprovePhase = {
					case_id: id as string,
					lawyer_stepper_id: phaseId,
					stepper_form_id: stepId,
				};
				await auditService.postApprovePhase(data);
				toastSuccess('The case section has been successfully approved.');
				refetchQueries();
			}
		} catch (error) {
			toastError(
				'An error occurred while confirming approval. Please try again.'
			);
		}
	};

	const isPayProcessing = React.useMemo(() => {
		return (paymentSummary?.data?.pending_count ?? 0) > 0;
	}, [paymentSummary]);

	return (
		<div className="flex flex-col space-y-8">
			{settlementDetail && (
				<div className="w-[336px]">
					<InputList
						title="Payment Method"
						name="payment_method"
						register={register}
						options={options}
						handleChange={(value) => {
							handleSelectMethod(value as PaymentMethodEnum);
						}}
						disabled={isCaseReadOnly}
						myDefaultValue={settlementDetail?.data.payment_method}
					/>
				</div>
			)}
			{optionSelected === PaymentMethodEnum.INDIVIDUAL && (
				<div className="!mt-8">
					<PayIndividually
						inputError={inputError}
						users={users}
						groupAmount={groupAmount}
						paymentMethod={paymentMethod}
						isDisabled={isApproved}
						setInputError={setInputError}
						getValues={getValues}
						register={register}
						setValue={setValue}
						setSearch={setSearch}
						search={search}
						handleSave={handleSave}
						paymentSummary={paymentSummary?.data}
						totalUsers={totalItems}
						lastItemCallback={lastItemCallback}
						refetchUserData={refetchUserData}
					/>
				</div>
			)}

			{optionSelected === PaymentMethodEnum.GROUP && (
				<div className="flex-1">
					<PaymentGroup
						usersList={usersList}
						isDisabled={isApproved}
						setError={setError}
						setUsersList={setUsersList}
						getValues={getValues}
						register={register}
						setValue={setValue}
					/>
				</div>
			)}

			{error?.message && (
				<div className="flex items-start gap-2">
					<Icon src={Icons.infoCircle} className="!size-5 text-main-red" />
					<Typography type="regular-12" className="text-main-red mt-1">
						{error.message}
					</Typography>
				</div>
			)}

			{!isApproved && optionSelected !== PaymentMethodEnum.EXTERNAL && (
				<Button
					label="Pay Settlement"
					decoration="primary"
					size="fit"
					onClick={showPayGroupConfirmModal}
					disabled={
						optionSelected === PaymentMethodEnum.GROUP && !watch('amount')
					}
				/>
			)}

			{isApproved && paymentMethod !== PaymentMethodEnum.EXTERNAL ? (
				isPayProcessing ? (
					<div className="bg-grey-5 rounded-xl py-1 px-3 flex items-center gap-2 w-fit">
						<Icon src={Icons.timer} className="!size-6 !text-primary" />
						<Typography type="regular-10" className="text-opacity-black">
							Processing
						</Typography>
					</div>
				) : (
					<div className="flex items-center gap-2">
						<div className="bg-grey-5 rounded-xl py-1 px-3 flex items-center gap-2 w-fit">
							<Icon src={Icons.moneySend} className="!size-6 !text-primary" />
							<Typography type="regular-10" className="text-opacity-black">
								Settlement paid
							</Typography>
						</div>
						<Typography
							type="regular-10"
							className="text-opacity-black leading-3"
						>
							Sent <br />{' '}
							{paymentSummary?.data.last_payment_date
								? format(
										parseISO(paymentSummary?.data.last_payment_date),
										'MMM dd, yyyy'
								  )
								: ''}
						</Typography>
					</div>
				)
			) : null}

			<ModalPayGroupConfirm
				title={<ModalHeader title="Confirm Payment Settlement" />}
				classNameCardContainer="max-w-[532px]"
				withXButton
			>
				<PayConfirmModal
					paymentMethod={optionSelected}
					users={users}
					confirmPaySettlement={handlePaySettlement}
					hideModal={hidePayGroupConfirmModal}
					totalSettlementAmount={
						optionSelected === PaymentMethodEnum.GROUP
							? Number(getValues('amount')) * (users?.length ?? 0)
							: settlementSummary?.settlement_amount ?? 0
					}
				/>
			</ModalPayGroupConfirm>
		</div>
	);
};

// import React from 'react';
// import clsx from 'clsx';
// import { useRouter } from 'next/router';
// import { useForm } from 'react-hook-form';
// import {
// 	QueryObserverResult,
// 	RefetchOptions,
// 	useQuery,
// 	useQueryClient,
// } from '@tanstack/react-query';

// import { useNotification, usePhaseStatus } from '@/hooks';
// import { RadioGroupItem, RadioGroup } from '@/components/ui/radio-group';
// import { Icon, Typography } from '@/components/common';
// import { ButtonsForm } from '@/components/dashboard';

// import Icons from '@/const/icons';
// import { PayIndividually } from './PayIndividually';
// import useModalPreviewPage from '@/hooks/useModalPreviewPage';
// import { CaseResolvedPreview } from '../../../email-preview/CaseResolvedPreview';
// import { formatCurrencyAlt } from '@/utils';
// import {
// 	auditService,
// 	casesLawyerService,
// 	GET_CASE_STEPS_STATUS,
// 	GET_LAWYER_CASE_DETAILS,
// 	GET_USERS_TO_PAY,
// } from '@/api';
// import {
// 	ApprovePhase,
// 	CaseDetailsLawyer,
// 	CaseSettlementDetails,
// 	HttpResponse,
// 	PaymentMethodEnum,
// 	StepStatusCaseData,
// 	StepStatusCaseType,
// 	UserToPayItem,
// } from '@/interfaces';
// import { PaymentGroup } from './PaymentGroup';
// import { CaseContext } from '@/context/CaseContext';

// interface Props {
// 	caseDetails: CaseDetailsLawyer | undefined;
// 	caseSteps?: StepStatusCaseData;
// 	statusStep: StepStatusCaseType;
// 	settlementDetail: HttpResponse<CaseSettlementDetails> | undefined;
// 	refetchSettlement: (
// 		options?: RefetchOptions | undefined
// 	) => Promise<QueryObserverResult<HttpResponse<CaseSettlementDetails>, Error>>;
// }

// export const PaymentDetails: React.FC<Props> = ({
// 	caseDetails,
// 	caseSteps,
// 	statusStep,
// 	settlementDetail,
// 	refetchSettlement,
// }) => {
// 	const options = [
// 		{
// 			id: '1',
// 			value: PaymentMethodEnum.GROUP,
// 			label: 'Pay Group',
// 		},
// 		{
// 			id: '2',
// 			value: PaymentMethodEnum.INDIVIDUAL,
// 			label: 'Pay Individually',
// 		},
// 		{
// 			id: '2',
// 			value: PaymentMethodEnum.EXTERNAL,
// 			label: 'External Administrator',
// 		},
// 	];

// 	const router = useRouter();
// 	const queryClient = useQueryClient();
// 	const { isCaseReadOnly } = React.useContext(CaseContext);
// 	const { toastError, toastSuccess } = useNotification();
// 	const { ModalPreviewPage, hidePreviewPage, showPreviewPage } =
// 		useModalPreviewPage();

// 	const { id } = router.query;
// 	const [usersList, setUsersList] = React.useState<UserToPayItem[]>([]);
// 	const [error, setError] = React.useState<{ message: string } | null>(null);
// 	const [inputError, setInputError] = React.useState<{
// 		message: string;
// 	} | null>(null);
// 	const [optionSelected, setOptionSelected] =
// 		React.useState<PaymentMethodEnum>();

// 	const [, setIsAmountUpdated] = React.useState<boolean>(false);

// 	const { phaseId, stepId } = usePhaseStatus({
// 		caseSteps,
// 		phaseName: 'Settlement Phase',
// 		stepName: 'Payment Details',
// 	});

// 	const { data: users, refetch: refetchUserData } = useQuery({
// 		queryKey: [GET_USERS_TO_PAY],
// 		queryFn: () => casesLawyerService.getUsersToPay(id as string),
// 	});

// 	const { getValues, watch, reset, register, setValue } = useForm();

// 	const updatedAmount = watch('amount');

// 	const paymentMethod = settlementDetail?.data.payment_method;
// 	const groupAmount = settlementDetail?.data.group_amount;
// 	const isPreview =
// 		paymentMethod !== PaymentMethodEnum.GROUP ||
// 		(paymentMethod === PaymentMethodEnum.GROUP && groupAmount !== 0);

// 	React.useEffect(() => {
// 		if (!paymentMethod) return;

// 		if (isPreview) {
// 			setOptionSelected(paymentMethod);
// 		}
// 	}, [paymentMethod]);

// 	React.useEffect(() => {
// 		if (!users?.data) return;
// 		const hasAmount = paymentMethod !== PaymentMethodEnum.EXTERNAL;

// 		setUsersList(
// 			users.data.items.map((item) => ({
// 				...item,
// 				formattedAmount: hasAmount
// 					? formatCurrencyAlt(String(item?.amount), 'blur')
// 					: '',
// 				error: null,
// 			}))
// 		);
// 	}, [users]);

// 	React.useEffect(() => {
// 		if (isPreview) {
// 			const isUpdated = getIsAmountChanged();
// 			setIsAmountUpdated(isUpdated);
// 		}
// 	}, [updatedAmount]);

// 	React.useEffect(() => {
// 		reset({
// 			amount: groupAmount,
// 		});
// 	}, [groupAmount]);

// 	const refetchQueries = async () => {
// 		await refetchSettlement();
// 		await refetchUserData();
// 		queryClient.refetchQueries({ queryKey: [GET_CASE_STEPS_STATUS] });
// 		queryClient.refetchQueries({ queryKey: [GET_LAWYER_CASE_DETAILS] });
// 	};

// 	const getPaymentInfo = () => {
// 		const currentAmount = (getValues('amount') || '0').toString();
// 		const amount =
// 			optionSelected === PaymentMethodEnum.GROUP
// 				? Number(currentAmount.replace(/[^0-9.]/g, ''))
// 				: 0;
// 		const isUpdatedAmount =
// 			optionSelected === PaymentMethodEnum.GROUP ||
// 			optionSelected === PaymentMethodEnum.EXTERNAL;

// 		return {
// 			isUpdatedAmount,
// 			amount,
// 		};
// 	};

// 	const handleComplete = async () => {
// 		const isError = handleIsError();
// 		if (isError) return;

// 		const { amount, isUpdatedAmount } = getPaymentInfo();
// 		try {
// 			const data = {
// 				payment_method: optionSelected as PaymentMethodEnum,
// 				user_payments: isUpdatedAmount
// 					? usersList.map((user) => ({ ...user, amount }))
// 					: usersList,
// 				group_amount: optionSelected === PaymentMethodEnum.GROUP ? amount : 0,
// 			};
// 			await casesLawyerService.completePaymentUsers(id as string, data);
// 			setIsAmountUpdated(false);
// 			refetchQueries();
// 			toastSuccess('The case section has been successfully completed.');
// 		} catch (error) {
// 			toastError(
// 				'An error occurred while confirming completion. Please try again.'
// 			);
// 		}
// 	};

// 	const handleSave = async () => {
// 		const isError = handleIsError();
// 		if (isError) return;
// 		const { amount, isUpdatedAmount } = getPaymentInfo();
// 		try {
// 			const data = {
// 				payment_method: optionSelected as PaymentMethodEnum,
// 				user_payments: isUpdatedAmount
// 					? usersList.map((user) => ({ ...user, amount }))
// 					: usersList,
// 				group_amount: optionSelected === PaymentMethodEnum.GROUP ? amount : 0,
// 			};
// 			await casesLawyerService.savePaymentUsers(id as string, data);
// 			refetchQueries();
// 			setIsAmountUpdated(false);
// 			toastSuccess(getSuccessMessage());
// 		} catch (error) {
// 			toastError(
// 				'An error occurred while saving the amounts. Please try again.'
// 			);
// 		}
// 	};

// 	const getSuccessMessage = (): string => {
// 		if (optionSelected === PaymentMethodEnum.GROUP) {
// 			return 'The amounts has been successfully saved.';
// 		}

// 		if (optionSelected === PaymentMethodEnum.INDIVIDUAL) {
// 			return 'The amount has been successfully saved.';
// 		}

// 		if (optionSelected === PaymentMethodEnum.EXTERNAL) {
// 			return 'The case section has been successfully saved.';
// 		}

// 		return '';
// 	};

// 	const handleIsError = (): boolean => {
// 		if (!optionSelected) {
// 			setError({
// 				message: 'Please fill out the required field.',
// 			});
// 			return true;
// 		}

// 		if (usersList.length === 0) return true;

// 		if (optionSelected === PaymentMethodEnum.GROUP) {
// 			const currentAmount: string = getValues('amount');
// 			if (!currentAmount) {
// 				setInputError({
// 					message: 'Please fill out the required field.',
// 				});
// 				return true;
// 			}

// 			if (currentAmount.startsWith('0')) {
// 				setInputError({
// 					message: 'Please enter an amount greater than 0.',
// 				});
// 				return true;
// 			}
// 		}

// 		if (optionSelected === PaymentMethodEnum.INDIVIDUAL) {
// 			let isErrorEmpty = 0;
// 			const count = usersList.reduce((acc, item) => {
// 				if (item.amount === null) isErrorEmpty += 1;
// 				return !item.amount || item.amount <= 0 ? acc + 1 : acc;
// 			}, 0);

// 			const isSomeEmpty = usersList.length == 0 || count > 0;
// 			if (optionSelected === PaymentMethodEnum.INDIVIDUAL && isSomeEmpty) {
// 				if (isErrorEmpty > 0) {
// 					const newUserList = usersList.map((user) => {
// 						const newUser = { ...user };
// 						newUser.error =
// 							user.amount === null
// 								? { ...newUser.error, message: 'true' }
// 								: { ...newUser.error, message: '' };
// 						return newUser;
// 					});

// 					setUsersList(newUserList);
// 					setError({
// 						message: `There are ${isErrorEmpty} clients with pending payment details. Please ensure all details are entered to proceed.`,
// 					});
// 				}
// 				if (!isErrorEmpty) {
// 					const newUserList = usersList.map((user) => {
// 						const newUser = { ...user };
// 						newUser.error =
// 							!user.amount || user.amount <= 0
// 								? { ...newUser.error, message: 'true' }
// 								: { ...newUser.error, message: '' };
// 						return newUser;
// 					});
// 					setUsersList(newUserList);

// 					setError({
// 						message: `There are ${count} users with a payment amount of zero. Please set an amount greater than zero.`,
// 					});
// 				}

// 				return true;
// 			}
// 		}

// 		setError(null);
// 		return false;
// 	};

// 	const handleApprove = async () => {
// 		try {
// 			if (id && phaseId && stepId) {
// 				const data: ApprovePhase = {
// 					case_id: id as string,
// 					lawyer_stepper_id: phaseId,
// 					stepper_form_id: stepId,
// 				};
// 				await auditService.postApprovePhase(data);
// 				toastSuccess('The case section has been successfully approved.');
// 				hidePreviewPage();
// 				refetchQueries();
// 			}
// 		} catch (error) {
// 			toastError(
// 				'An error occurred while confirming approval. Please try again.'
// 			);
// 		}
// 	};

// 	const handleSelectMethod = (method: PaymentMethodEnum) => {
// 		setOptionSelected(method);
// 		setError(null);
// 	};

// 	const getIsAmountChanged = () => {
// 		if (usersList.length === 0) return false;

// 		if (optionSelected === PaymentMethodEnum.GROUP) {
// 			const currentAmount = (getValues('amount') || 0)?.toString();
// 			const amount = Number(currentAmount?.replace(/[^0-9.]/g, ''));
// 			return amount !== usersList[0].amount;
// 		}

// 		if (optionSelected === PaymentMethodEnum.INDIVIDUAL) {
// 			return usersList.some((user) => {
// 				const find = users?.data.items.find(
// 					(item) => item.application_id === user.application_id
// 				);
// 				if (find) {
// 					return find.amount !== user.amount;
// 				}
// 				return false;
// 			});
// 		}

// 		return false;
// 	};

// 	const isGeneralDisabled = isCaseReadOnly;
// 	const isDefault = statusStep === 'default';
// 	const isCompleted = statusStep === 'completed';
// 	const isApproved = statusStep === 'approved';
// 	const isValueAmountChange = getIsAmountChanged();

// 	const isValueChanged =
// 		paymentMethod !== optionSelected || isValueAmountChange;

// 	const isPreviewEnable = (isCompleted || isApproved) && !isValueChanged;
// 	const isSaveEnable =
// 		!isGeneralDisabled &&
// 		((isDefault && paymentMethod) || (!isDefault && isValueChanged));

// 	const isButtonsActionsDisabled = !optionSelected;

// 	return (
// 		<div className="space-y-8">
// 			<div className="space-y-2 w-[28.43rem]">
// 				<Typography type="regular-16">Payment Method</Typography>
// 				<div className="border border-opacity-primary p-6 space-y-4 rounded-2xl">
// 					{options.map((option, index) => {
// 						return (
// 							<RadioGroup
// 								key={index}
// 								value={optionSelected}
// 								disabled={isCaseReadOnly}
// 								onValueChange={handleSelectMethod}
// 								orientation="vertical"
// 								className={clsx('flex flex-col')}
// 							>
// 								<div className="flex items-baseline space-x-2 w-full">
// 									<RadioGroupItem
// 										size={'sm'}
// 										value={option.value}
// 										id={option.id}
// 										className={clsx(
// 											'bg-gradient-to-b from-[#FFFFFF] to-[#F4F4F9] border-opacity-primary h-4 w-4 mr-2 mt-2'
// 										)}
// 									/>
// 									<div className="flex items-baseline gap-2 w-full">
// 										<div className="w-max mt-2">
// 											<label
// 												htmlFor={option.value}
// 												className="text-black text-base"
// 											>
// 												{option.label}
// 											</label>
// 										</div>
// 										{optionSelected === PaymentMethodEnum.GROUP &&
// 											option.value === PaymentMethodEnum.GROUP && (
// 												<div className="flex-1">
// 													<PaymentGroup
// 														inputError={inputError}
// 														users={users?.data.items}
// 														groupAmount={groupAmount}
// 														paymentMethod={paymentMethod}
// 														setInputError={setInputError}
// 														getValues={getValues}
// 														register={register}
// 														setValue={setValue}
// 													/>
// 												</div>
// 											)}
// 									</div>
// 								</div>
// 							</RadioGroup>
// 						);
// 					})}
// 				</div>
// 				{optionSelected === PaymentMethodEnum.INDIVIDUAL && (
// 					<div className="!mt-8">
// 						<PayIndividually
// 							setError={setError}
// 							usersList={usersList}
// 							setUsersList={setUsersList}
// 						/>
// 					</div>
// 				)}

// 				{error?.message && (
// 					<div className="flex items-start gap-2">
// 						<Icon src={Icons.infoCircle} className="!size-5 text-main-red" />
// 						<Typography type="regular-12" className="text-main-red mt-1">
// 							{error.message}
// 						</Typography>
// 					</div>
// 				)}
// 			</div>

// 			<ModalPreviewPage
// 				handleClickApprove={handleApprove}
// 				handleClickBack={hidePreviewPage}
// 				handleClickEdit={hidePreviewPage}
// 				title={isApproved ? 'This is your preview page.' : undefined}
// 				hideApproveButton={isApproved}
// 				hideEditButton={isApproved}
// 			>
// 				<CaseResolvedPreview caseName={caseDetails?.title} />
// 			</ModalPreviewPage>
// 			<ButtonsForm
// 				isCompleted={(isCompleted || isApproved) && !isValueChanged}
// 				isSaveDisabled={!isSaveEnable}
// 				isPreviewDisabled={!isPreviewEnable}
// 				isCompleteDisabled={isApproved || isCompleted || isCaseReadOnly}
// 				saveButtonClassName={clsx({
// 					'!bg-[#B18EE0] !border-[#B18EE0] hover:!bg-[#B18EE0] focus:!bg-[#B18EE0] cursor-default !text-white':
// 						isButtonsActionsDisabled,
// 				})}
// 				completeButtonClassName={clsx({
// 					'!bg-[#B18EE0] !border-[#B18EE0] hover:!bg-[#B18EE0] focus:!bg-[#B18EE0] cursor-default !text-white':
// 						isButtonsActionsDisabled,
// 				})}
// 				onClickComplete={handleComplete}
// 				onClickSave={handleSave}
// 				onClickPreview={() => showPreviewPage()}
// 			/>
// 		</div>
// 	);
// };
